/**
 * DTS (Details Tab Strips) slice — the central coordinator for the tabbed detail
 * panel. Manages the tab list, which tab is active, per-tab selected rows, view
 * mode, and hierarchical submenu navigation.
 *
 * Key design points:
 *  - selectedRowsByDts is keyed by dtsId so each tab independently tracks the
 *    row the user clicked in its grid; child tabs read their ancestor's row via
 *    selectAncestorSelectedRow to scope their own fetch.
 *  - checkedRowsByDts stores multi-selected rows for bulk actions (e.g. delete);
 *    it is cleared by clearSelectedRowsForMms and clearDtsListForRefetch.
 *  - clearDtsListForRefetch intentionally preserves activeTab / activeTabLabel so
 *    the UI stays on the current tab after a BA/language-driven refetch completes.
 *  - enterSubmenu / exitSubmenu implement a stack-based breadcrumb so the user can
 *    drill into nested submenus and back out to the parent level.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import detailsTabStripsService, {
    DetailsTabStripsResponse,
} from '@/features/it-process/application-management/dts/services/DetailsTabStripsService'
import { ObjResponse } from '@/features/it-process/application-management/sdui/services/ObjService'
import { RootState } from '@/store'

export interface CurrentActiveTabDetails {
    dtsId: number | null
    dtsName: string | null
}

export interface DetailsTabStripsState {
    tabsList: DetailsTabStripsResponse[]
    activeTab: string
    activeTabLabel: string
    currentActiveTabDetails: CurrentActiveTabDetails
    urlSyncEnabled: boolean
    loading: boolean
    error: string | null
    currentMmsId: number | null
    selectedRowsByDts: Record<number, ObjResponse | null>
    /** OBJName of the grid that produced each DTS's selected row (selection provenance). */
    selectedRowSourceByDts: Record<number, string | null>
    /** Checkbox (multi-select) rows per DTS � drives bulk actions like delete. Not persisted. */
    checkedRowsByDts: Record<number, ObjResponse[]>
    /** True when a DTS's grid last fetched ZERO rows � the breadcrumb shows "No Selected Data" for it. */
    gridEmptyByDts: Record<number, boolean>
    viewMode: 'cards' | 'table'
    // Hierarchical submenu support
    currentParentDtsId: number | null // Currently viewing submenu of this parent (null = root level)
    submenuHistory: number[] // Stack of parent IDs for breadcrumb navigation
    /**
     * Params of the most recent fetchDetailsTabStrips dispatch (set on pending,
     * KEPT on rejected so a failure doesn't auto-retry in a loop). Lets the two
     * dispatchers (useNavigationInit's init fetch, useDtsSync's menu-change
     * fetch) see each other's in-flight/settled request and skip issuing a
     * wire-identical duplicate. Cleared when the tab list is reset for a
     * refetch. Never persisted — rehydration rebuilds from initialState.
     */
    lastRequestedParams: FetchDtsParams | null
}

export const initialState: DetailsTabStripsState = {
    tabsList: [],
    activeTab: 'tab1',
    activeTabLabel: 'General',
    currentActiveTabDetails: {
        dtsId: null,
        dtsName: null,
    },
    urlSyncEnabled: true,
    loading: false,
    error: null,
    currentMmsId: null,
    selectedRowsByDts: {},
    selectedRowSourceByDts: {},
    checkedRowsByDts: {},
    gridEmptyByDts: {},
    viewMode: 'cards',
    currentParentDtsId: null,
    submenuHistory: [],
    lastRequestedParams: null,
}

export interface FetchDtsParams {
    mmsId: number
    languageId?: number
    baId?: number
}

// Async thunk to fetch DTS data from API
export const fetchDetailsTabStrips = createAsyncThunk(
    'dts/fetchDetailsTabStrips',
    async (params: FetchDtsParams, { rejectWithValue }) => {
        try {
            const response = await detailsTabStripsService.getDetailsTabStrips(
                params.mmsId,
                params.languageId,
                params.baId,
            )
            return { data: response, mmsId: params.mmsId }
        } catch (error) {
            return rejectWithValue(
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch details tab strips',
            )
        }
    },
)

export const detailsTabStripsSlice = createSlice({
    name: 'dts',
    initialState,
    reducers: {
        setTabsList: (
            state,
            action: PayloadAction<DetailsTabStripsResponse[]>,
        ) => {
            state.tabsList = action.payload
        },
        setActiveTab: (state, action: PayloadAction<string>) => {
            state.activeTab = action.payload
            // Find the tab label from the tabsList
            const tab = state.tabsList.find((t) => t.value === action.payload)
            if (tab) {
                state.activeTabLabel = tab.label
            }
        },
        setActiveTabLabel: (state, action: PayloadAction<string>) => {
            state.activeTabLabel = action.payload
            // Find the tab value from the tabsList
            const tab = state.tabsList.find((t) => t.label === action.payload)
            if (tab) {
                state.activeTab = tab.value
            }
        },
        setCurrentActiveTabDetails: (
            state,
            action: PayloadAction<CurrentActiveTabDetails>,
        ) => {
            state.currentActiveTabDetails = action.payload
        },
        setViewMode: (state, action: PayloadAction<'cards' | 'table'>) => {
            state.viewMode = action.payload
        },
        setSelectedRowForDts: (
            state,
            action: PayloadAction<{
                dtsId: number
                row: ObjResponse | null
                /** OBJName of the grid that produced the row, for label/provenance. */
                sourceObjName?: string
            }>,
        ) => {
            const { dtsId, row, sourceObjName } = action.payload
            state.selectedRowsByDts[dtsId] = row
            state.selectedRowSourceByDts[dtsId] = row
                ? sourceObjName ?? null
                : null
        },
        /**
         * Recorded by a DTS's grid after every completed fetch: true when it got
         * ZERO rows. Drives the breadcrumb's "[Tab] - No Selected Data" segment.
         */
        setGridEmptyForDts: (
            state,
            action: PayloadAction<{ dtsId: number; isEmpty: boolean }>,
        ) => {
            const { dtsId, isEmpty } = action.payload
            if (isEmpty) {
                state.gridEmptyByDts[dtsId] = true
            } else {
                delete state.gridEmptyByDts[dtsId]
            }
        },
        clearSelectedRowsForMms: (state) => {
            state.selectedRowsByDts = {}
            state.selectedRowSourceByDts = {}
            state.checkedRowsByDts = {}
            state.gridEmptyByDts = {}
        },
        setCheckedRowsForDts: (
            state,
            action: PayloadAction<{ dtsId: number; rows: ObjResponse[] }>,
        ) => {
            const { dtsId, rows } = action.payload
            // Always set (even empty) so key presence signals "user has interacted
            // with checkboxes for this DTS" — lets delete handlers distinguish
            // "never touched checkboxes" (key absent) from "user unchecked all" (key = []).
            state.checkedRowsByDts[dtsId] = rows
        },
        /**
         * Removes the DTS's checked-rows entry entirely (unlike setCheckedRowsForDts
         * with an empty array, which leaves a "user interacted" marker behind).
         * Used in two places: when the grid's ancestor scope changes (e.g. switching
         * Admin) — rows checked under the PREVIOUS parent are no longer valid — and
         * on a genuine fresh visit to a tab, so a stale "user explicitly cleared
         * everything" flag from a previous visit doesn't outlive the grid remount
         * that visually re-selects the default row. Both cases reset to "never
         * interacted" so the delete flow falls back to the freshly auto-selected
         * row instead of silently no-oping.
         */
        clearCheckedRowsForDts: (
            state,
            action: PayloadAction<{ dtsId: number }>,
        ) => {
            delete state.checkedRowsByDts[action.payload.dtsId]
        },
        setUrlSyncEnabled: (state, action: PayloadAction<boolean>) => {
            state.urlSyncEnabled = action.payload
        },
        clearDtsError: (state) => {
            state.error = null
        },
        // Submenu navigation actions
        enterSubmenu: (state, action: PayloadAction<number>) => {
            // action.payload is the parent dtsId to enter
            if (!Array.isArray(state.submenuHistory)) state.submenuHistory = []
            state.submenuHistory.push(state.currentParentDtsId ?? 0)
            state.currentParentDtsId = action.payload
            state.activeTab = '' // Reset active tab when entering submenu
        },
        exitSubmenu: (state) => {
            // Go back to parent level
            const parentId = state.submenuHistory.pop()
            state.currentParentDtsId = parentId === 0 ? null : parentId ?? null
        },
        resetSubmenuNavigation: (state) => {
            // Reset to root level

            state.currentParentDtsId = null
            state.submenuHistory = []
        },
        resetDetailsTabStripsState: (state) => {
            state.tabsList = []
            state.activeTab = 'tab1'
            state.activeTabLabel = 'General'
            state.currentActiveTabDetails = {
                dtsId: null,
                dtsName: null,
            }
            state.urlSyncEnabled = true
            state.loading = false
            state.error = null
            state.currentMmsId = null
            state.selectedRowsByDts = {}
            state.selectedRowSourceByDts = {}
            state.checkedRowsByDts = {}
            state.gridEmptyByDts = {}
            state.viewMode = 'cards'
            state.currentParentDtsId = null
            state.submenuHistory = []
            state.lastRequestedParams = null
        },
        // Used on language/BA change: clears stale tab data without resetting
        // stays on the same tab after the refetch completes.
        //
        // preserveSelection: a LANGUAGE change re-fetches the SAME records (same
        // BA) only re-localised, so the user's per-tab row selection should
        // survive — passing true keeps selectedRowsByDts/source/checked so the
        // grid can re-select the same row (by id) from the fresh data instead
        // of snapping back to the first row. A BA change omits it (defaults
        // false): different BA = different data, so the old selection is void.
        clearDtsListForRefetch: (
            state,
            action: PayloadAction<{ preserveSelection?: boolean } | undefined>,
        ) => {
            const preserveSelection = action.payload?.preserveSelection ?? false
            state.tabsList = []
            state.loading = false
            state.error = null
            state.currentMmsId = null
            if (!preserveSelection) {
                state.selectedRowsByDts = {}
                state.selectedRowSourceByDts = {}
                state.checkedRowsByDts = {}
            }
            state.gridEmptyByDts = {}
            state.currentParentDtsId = null
            state.submenuHistory = []
            state.lastRequestedParams = null
            // activeTab and activeTabLabel are intentionally preserved
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDetailsTabStrips.pending, (state, action) => {
                state.lastRequestedParams = action.meta.arg
                state.loading = true
                state.error = null
            })
            .addCase(
                fetchDetailsTabStrips.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        data: DetailsTabStripsResponse[]
                        mmsId: number
                    }>,
                ) => {
                    // A response for a menu the user has since navigated away
                    // from must not clobber the newer menu's tabs (fast A→B→A
                    // switching can land A's slow response after B's).
                    if (
                        state.lastRequestedParams &&
                        action.payload.mmsId !== state.lastRequestedParams.mmsId
                    ) {
                        return
                    }
                    state.tabsList = action.payload.data
                    state.currentMmsId = action.payload.mmsId
                    state.loading = false
                    state.error = null
                },
            )
            .addCase(fetchDetailsTabStrips.rejected, (state, action) => {
                // lastRequestedParams is intentionally KEPT: clearing it would
                // let the useDtsSync gate re-dispatch the same failed request
                // in a render loop.
                state.loading = false
                state.error = action.payload as string
                state.tabsList = [] // Clear tabs on error to prevent stale data
            })
    },
})

export const {
    setTabsList,
    setActiveTab,
    setActiveTabLabel,
    setCurrentActiveTabDetails,
    setSelectedRowForDts,
    setGridEmptyForDts,
    setCheckedRowsForDts,
    clearCheckedRowsForDts,
    clearSelectedRowsForMms,
    setViewMode,
    setUrlSyncEnabled,
    clearDtsError,
    enterSubmenu,
    exitSubmenu,
    resetSubmenuNavigation,
    resetDetailsTabStripsState,
    clearDtsListForRefetch,
} = detailsTabStripsSlice.actions
export const selectDtsList = (state: RootState) =>
    state.dts?.tabsList ?? []
export const selectDtsLastRequestedParams = (state: RootState) =>
    state.dts?.lastRequestedParams ?? null
export const selectDtsLoading = (state: RootState) =>
    state.dts?.loading ?? false
export const selectDtsError = (state: RootState) =>
    state.dts?.error ?? null
export const selectActiveTab = (state: RootState) =>
    state.dts?.activeTab ?? 'tab1'
export const selectActiveTabLabel = (state: RootState) =>
    state.dts?.activeTabLabel ?? 'General'
export const selectCurrentMmsId = (state: RootState) =>
    state.dts?.currentMmsId ?? null
export const selectCurrentActiveTabDetails = (state: RootState) =>
    state.dts?.currentActiveTabDetails ?? {
        dtsId: null,
        dtsName: null,
    }
export const selectSelectedRowForDts = (dtsId: number) => (state: RootState) =>
    state.dts?.selectedRowsByDts?.[dtsId] ?? null
/** Checkbox (multi-select) rows for a DTS — drives bulk actions like delete. */
export const selectCheckedRowsForDts =
    (dtsId: number) =>
    (state: RootState): ObjResponse[] =>
        state.dts?.checkedRowsByDts?.[dtsId] ?? []
/**
 * True when the user has explicitly interacted with the checkboxes for this DTS
 * (i.e. `setCheckedRowsForDts` has been dispatched at least once). False on first
 * load when only the auto-selected row is active and no checkbox has been touched.
 */
export const selectHasCheckboxInteraction =
    (dtsId: number | undefined) =>
    (state: RootState): boolean =>
        dtsId != null && dtsId in (state.dts?.checkedRowsByDts ?? {})
/** Whole map of per-DTS selected rows � lets the breadcrumb read every level's selection. */
export const selectSelectedRowsByDts = (state: RootState) =>
    state.dts?.selectedRowsByDts ?? {}
/** Whole map of per-DTS selection provenance (source grid OBJName), keyed by dtsId. */
export const selectSelectedRowSourceByDts = (state: RootState) =>
    state.dts?.selectedRowSourceByDts ?? {}
/** Per-DTS "grid fetched zero rows" flags � drives the breadcrumb's "No Selected Data" segment. */
export const selectGridEmptyByDts = (state: RootState) =>
    state.dts?.gridEmptyByDts ?? {}
export interface AncestorSelectedRowInfo {
    /** dtsId of the ancestor tab whose grid the row was selected on. */
    dtsId: number
    row: ObjResponse
    /** OBJName of the grid that produced the row, when recorded. */
    sourceObjName?: string
}

/**
 * Walk up the dtsParentId chain � starting at the given tab's PARENT � and
 * return the first stored selected row found, along with the ancestor dtsId it
 * was stored on and the OBJName of the grid that produced it. Lets a nested
 * subtab read the row selected on an ANCESTOR tab (e.g. the Admin grid several
 * levels up) AND know where it came from (for labels / jump-back navigation).
 *
 * Self is intentionally skipped: a grid stores its own clicked row under its own
 * dtsId (so its child subtabs can read it via this selector on drill-in), but
 * the grid itself must NOT re-read that row as its own scope. Doing so would
 * re-scope the grid to the clicked row and, for a grid with `rowParams` (e.g. the
 * license lists scoped by licId/licType), drop the params the clicked row
 * doesn't carry � refetching empty. A grid always stays scoped by its ancestor.
 */
export const selectAncestorSelectedRowInfo =
    (dtsId?: number) =>
    (state: RootState): AncestorSelectedRowInfo | null => {
        const byDts = state.dts?.selectedRowsByDts ?? {}
        const srcByDts = state.dts?.selectedRowSourceByDts ?? {}
        const list = state.dts?.tabsList ?? []
        const seen = new Set<number>()
        // Begin at the parent so the tab's own stored row is skipped (see above).
        let cur: number | undefined = list.find(
            (d) => d.dtsId === dtsId,
        )?.dtsParentId
        while (cur != null && !seen.has(cur)) {
            const row = byDts[cur]
            if (row) {
                return {
                    dtsId: cur,
                    row,
                    sourceObjName: srcByDts[cur] ?? undefined,
                }
            }
            seen.add(cur)
            cur = list.find((d) => d.dtsId === cur)?.dtsParentId
        }
        return null
    }

/**
 * The ancestor's selected row only (no provenance). Thin wrapper over
 * {@link selectAncestorSelectedRowInfo} � preserves the existing call sites
 * (DynamicScreen seeding, license DAL actions) that just need the row.
 */
export const selectAncestorSelectedRow =
    (dtsId?: number) =>
    (state: RootState): ObjResponse | null =>
        selectAncestorSelectedRowInfo(dtsId)(state)?.row ?? null
export const selectViewMode = (state: RootState) =>
    state.dts?.viewMode ?? 'cards'
// Submenu navigation selectors
export const selectCurrentParentDtsId = (state: RootState) =>
    state.dts?.currentParentDtsId ?? null
export const selectSubmenuHistory = (state: RootState) =>
    state.dts?.submenuHistory ?? []
export const selectIsInSubmenu = (state: RootState) =>
    (state.dts?.currentParentDtsId ?? null) !== null

export default detailsTabStripsSlice.reducer
